import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { useAuth } from '../App';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';

const PERSONS = ['Ladys', 'Vee', 'Norbi', 'Bösch', 'Geschlossen'];
const TIMES = Array.from({ length: 49 }, (_, i) => `${String(Math.floor(i / 2)).padStart(2, '0')}:${i % 2 ? '30' : '00'}`);

// Admin: add a new shift (draft without orig) or edit/delete an existing one.
export const ShiftEditDialog = ({ month, draft, onClose, onSaved }) => {
  const { API, language } = useAuth();
  const L = (en, de) => (language === 'de' ? de : en);
  const [person, setPerson] = useState('Vee');
  const [start, setStart] = useState('08:00');
  const [end, setEnd] = useState('16:00');
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!draft) return;
    setPerson(draft.person || 'Vee'); setStart(draft.start || '08:00'); setEnd(draft.end || '16:00');
  }, [draft]);
  if (!draft) return null;
  const editing = !!draft.orig;

  const save = async () => {
    setBusy(true);
    try {
      await API.post(`/work-plans/${month}/shifts`, { date: draft.date, person, start, end, orig: draft.orig || null });
      toast.success(editing ? L('Shift updated', 'Schicht geändert') : L('Shift added', 'Schicht eingetragen'));
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || L('Error', 'Fehler')); }
    finally { setBusy(false); }
  };
  const remove = async () => {
    if (!window.confirm(L('Delete this shift?', 'Diese Schicht löschen?'))) return;
    setBusy(true);
    try { await API.post(`/work-plans/${month}/shifts/delete`, draft.orig); toast.success(L('Shift deleted', 'Schicht gelöscht')); onSaved(); }
    catch (e) { toast.error(e.response?.data?.detail || L('Error', 'Fehler')); }
    finally { setBusy(false); }
  };
  const sel = 'w-full border border-slate-200 rounded-lg px-3 py-2 text-sm bg-white';

  return (
    <Dialog open={!!draft} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-white max-w-sm" data-testid="shift-edit-dialog">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold">{editing ? L('Edit shift', 'Schicht ändern') : L('New shift', 'Neue Schicht')}</DialogTitle>
          <DialogDescription>
            {new Date(`${draft.date}T00:00:00`).toLocaleDateString('de-CH', { weekday: 'long', day: '2-digit', month: '2-digit', year: 'numeric' })}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-3 gap-2">
            {PERSONS.map(p => (
              <button key={p} type="button" onClick={() => setPerson(p)} data-testid={`shift-edit-person-${p}`}
                className={`px-2 py-2 rounded-lg text-sm font-semibold border transition-colors ${person === p ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-700 border-slate-200 hover:bg-slate-50'}`}>
                {p}
              </button>
            ))}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <label className="text-xs font-semibold text-slate-500 space-y-1">
              <span>{L('From', 'Von')}</span>
              <select value={start} onChange={(e) => setStart(e.target.value)} className={sel} data-testid="shift-edit-start">
                {TIMES.slice(0, -1).map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </label>
            <label className="text-xs font-semibold text-slate-500 space-y-1">
              <span>{L('To', 'Bis')}</span>
              <select value={end} onChange={(e) => setEnd(e.target.value)} className={sel} data-testid="shift-edit-end">
                {TIMES.slice(1).map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </label>
          </div>
          <div className="flex gap-2">
            {editing && (
              <Button variant="outline" onClick={remove} disabled={busy} className="border-red-300 text-red-600 hover:bg-red-50" data-testid="shift-edit-delete-btn">
                {L('Delete', 'Löschen')}
              </Button>
            )}
            <Button variant="outline" onClick={onClose} className="flex-1">{L('Cancel', 'Abbrechen')}</Button>
            <Button onClick={save} disabled={busy} className="flex-1 bg-slate-900 text-white hover:bg-slate-700" data-testid="shift-edit-save-btn">
              {L('Save', 'Speichern')}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
